/**
 * The support page, as content without chrome (R22 T12).
 *
 * **Both frontends render this, and both must**: the App Store support URL and
 * the Play deletion-policy URL point at these pages, and a reviewer opens them.
 * Two copies of a compliance page is two chances for one of them to describe a
 * product Bookr does not ship, which is worse than having no page at all.
 *
 * Chrome is the host's: each frontend wraps this in its own header and footer.
 * Internal links take a `Link` component from the caller, so the static web app
 * keeps client-side navigation without this file importing a router.
 */
import type { StaticPageLink } from "./staticPage";

export function SupportContent({ Link }: { Link: StaticPageLink }) {
  return (
    <>

        <div className="grow bg-page px-4 py-12 text-body">
          <article className="mx-auto w-full max-w-2xl">
            <h1 className="font-display text-3xl font-bold text-ink sm:text-4xl">
              Support
            </h1>
            <p className="mt-3 text-lg">
              The questions Bookr actually generates, answered.
            </p>

            <Question q="I requested a time. Why does it say awaiting confirmation?">
              <p>
                Because that is what happened. Bookr does not book you in
                automatically: it sends your request to the clinic, and the clinic
                confirms or declines it. You get a notification in your inbox
                either way. Nothing is held for you until the clinic says yes.
              </p>
            </Question>

            <Question q="A time was showing and then it was refused.">
              <p>
                Open times are worked out the moment you look, from the
                practitioner&rsquo;s weekly schedule. Between the moment you saw a
                time and the moment you asked for it, somebody else can have taken
                it, or the practitioner can have changed their week. The app tells
                you which of those happened and leaves the rest of your request as
                it was, so you only have to pick another time.
              </p>
            </Question>

            <Question q="Can I cancel?">
              <p>
                Yes, up to an hour before the appointment starts. Inside that last
                hour, please call the clinic instead: at that point somebody has
                already arranged their day around you, and a cancellation that
                nobody sees in time is worse than a phone call.
              </p>
            </Question>

            <Question q="When can I leave a review?">
              <p>
                Fifteen minutes after the appointment was due to end. A rating is
                enough; writing something is optional. One review per appointment.
              </p>
            </Question>

            <Question q="Why can I not change my phone number?">
              <p>
                It is how you sign in and how a clinic knows the person arriving is
                the person who booked, so changing it means verifying the new one
                the same way you verified the first. Write to us and we will do it
                with you.
              </p>
            </Question>

            <Question q="My code never arrived.">
              <p>
                It comes over WhatsApp, not SMS, because that is what works
                reliably in Lebanon. Check the number you typed, including the
                leading zero, and use Resend. If it still does not arrive, write to
                us with the number.
              </p>
            </Question>

            <Question q="I run a clinic. How do I get on Bookr?">
              <p>
                Register on the web at{" "}
                <Link className="text-accent underline" href="/join">
                  bookrlb.com/join
                </Link>
                . The mobile app is for people booking care; the tools for running
                a clinic live on the web, and signing into the app with a clinic
                account will point you there.
              </p>
            </Question>

            <Question q="I want my account deleted.">
              <p>
                Do it yourself: <strong className="text-ink">Settings</strong> in
                the app, or your{" "}
                <Link className="text-accent underline" href="/profile">
                  profile page
                </Link>{" "}
                on the web. It asks for your password and happens immediately.
              </p>
              <p>
                Your name, phone number, password, inbox and any home-visit address
                go. Upcoming appointments are cancelled. Appointments you actually
                had stay in the clinic&rsquo;s own records with your name removed,
                and the star rating on any review stays without the words, so a
                business&rsquo;s public average cannot be changed after the fact.
                The screen says all of this before you confirm.
              </p>
            </Question>

            <div className="mt-12 rounded-xl border border-line bg-surface p-6">
              <h2 className="font-display text-lg font-bold text-ink">
                Still stuck
              </h2>
              <p className="mt-2 leading-relaxed">
                Write to{" "}
                <a className="text-accent underline" href="mailto:support@bookr.lb">
                  support@bookr.lb
                </a>
                . Tell us the phone number on your account and roughly when the
                problem happened, and we can find it.
              </p>
              <p className="mt-3 text-sm text-muted">
                See also our{" "}
                <Link className="text-accent underline" href="/privacy">
                  privacy policy
                </Link>
                .
              </p>
            </div>
          </article>
        </div>
    </>
  );
}

function Question({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div className="mt-8">
      <h3 className="font-display text-lg font-bold text-ink">{q}</h3>
      <div className="mt-2 space-y-2 leading-relaxed">{children}</div>
    </div>
  );
}
